import type { IndividualProfileSummary, IndividualProfilesResponse } from "@rbim/shared";
import { ArrowLeft, FileText, Printer, Search } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { ApiRequestError, apiRequest } from "../api";
import { buildIndividualRecordCertificateData } from "../features/certificates/certificate-data";
import { IndividualRecordCertificate } from "../features/certificates/individual-record-certificate";
import { Pagination } from "./household-profiles-page";

export function CertificatesPage() {
  const [response, setResponse] = useState<IndividualProfilesResponse | null>(null);
  const [draftSearch, setDraftSearch] = useState("");
  const [search, setSearch] = useState("");
  const [barangayId, setBarangayId] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<IndividualProfileSummary | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    const parameters = new URLSearchParams({ search, page: String(page), pageSize: "10" });
    if (barangayId) parameters.set("barangayId", barangayId);
    apiRequest<IndividualProfilesResponse>(`/certificates/residents?${parameters}`)
      .then((data) => { if (active) setResponse(data); })
      .catch((requestError) => { if (active) setError(requestError instanceof ApiRequestError ? requestError.message : "Certificate residents could not be loaded."); });
    return () => { active = false; };
  }, [barangayId, page, search]);

  function submitSearch(event: FormEvent<HTMLFormElement>) { event.preventDefault(); setPage(1); setSearch(draftSearch.trim()); }

  if (selected) {
    return <CertificatePreview resident={selected} onBack={() => setSelected(null)} />;
  }

  return <section className="portal-page certificates-page"><header className="page-heading"><div><span className="eyebrow">Authorized certificate search</span><h1>Certificates</h1><p>Select only residents returned from your Municipality/City's canonical RBIM records.</p></div><FileText className="page-heading-icon" /></header>
    <form className="profile-toolbar" onSubmit={submitSearch}><label><span>Search resident</span><span className="search-input"><Search /><input value={draftSearch} onChange={(event) => setDraftSearch(event.currentTarget.value)} placeholder="Resident name or household" /></span></label><label><span>Barangay</span><select value={barangayId} onChange={(event) => { setBarangayId(event.currentTarget.value); setPage(1); }}><option value="">All Barangays</option>{response?.barangays.map((barangay) => <option key={barangay.id} value={barangay.id}>{barangay.name}</option>)}</select></label><button className="primary-button" type="submit">Search</button></form>
    {error ? <div className="form-error" role="alert">{error}</div> : null}
    <div className="certificate-layout"><section className="table-card"><div className="responsive-table"><table><thead><tr><th>Resident</th><th>Barangay</th><th>Household</th><th /></tr></thead><tbody>{response?.items.length ? response.items.map((resident) => <tr key={resident.id}><td><strong>{resident.fullName}</strong><small>{resident.sex} · Age {resident.age ?? "—"}</small></td><td>{resident.barangayName}</td><td>{resident.householdCode || "—"}</td><td><button className="table-action" type="button" onClick={() => setSelected(resident)}>Generate Certificate</button></td></tr>) : <tr><td colSpan={4}>No residents match the current certificate search.</td></tr>}</tbody></table></div><Pagination page={response?.page ?? 1} totalPages={response?.totalPages ?? 1} total={response?.totalItems ?? 0} onPage={setPage} /></section></div>
  </section>;
}

export function CertificatePreview({ resident, onBack }: { resident: IndividualProfileSummary; onBack: () => void }) {
  const certificateData = buildIndividualRecordCertificateData(resident);
  return (
    <section className="portal-page individual-record-module certificate-preview-screen">
      <header className="certificate-preview-header">
        <button className="certificate-preview-header-button certificate-preview-back" type="button" onClick={onBack}>
          <ArrowLeft aria-hidden="true" />
          Back
        </button>
        <div className="certificate-preview-header-icon"><FileText aria-hidden="true" /></div>
        <div className="certificate-preview-header-copy">
          <span>Documents</span>
          <h1>Certificate Preview</h1>
          <p>Selected resident: {resident.fullName}</p>
        </div>
        <button className="certificate-preview-header-button print-certificate-button" type="button" onClick={() => window.print()}>
          <Printer aria-hidden="true" />
          Print
        </button>
      </header>
      <div className="individual-record-preview"><IndividualRecordCertificate data={certificateData} /></div>
    </section>
  );
}
